import React, { useState, useEffect } from 'react';
import { API, C } from '../../constants';
import { formatSize } from '../../utils';
import useWindowSize from '../../hooks/useWindowSize';
import EditInfoModal from '../EditInfoModal';
import UploadTab from '../upload/UploadTab';
import AdminInboxTab from './AdminInboxTab';
import AdminStatsTab from './AdminStatsTab';
import AdminSubtitlesTab from './AdminSubtitlesTab';

export default function AdminPanel({ token, user, onClose, onUploadDone, onSessionExpired }) {
  const [tab, setTab]                       = useState('users');
  const [users, setUsers]                   = useState([]);
  const [media, setMedia]                   = useState([]);
  const [mediaInfo, setMediaInfo]           = useState({});
  const [editingMedia, setEditingMedia]     = useState(null);
  const [newUser, setNewUser]               = useState({ name:'', email:'', password:'', role:'viewer' });
  const [msg, setMsg]                       = useState('');
  const [scanningTMDB, setScanningTMDB]     = useState(false);
  const [tmdbMsg, setTmdbMsg]               = useState('');
  const [tmdbLoadingMap, setTmdbLoadingMap] = useState({});
  const [expandedSessions, setExpandedSessions] = useState(null);
  const [sessionsMap, setSessionsMap]           = useState({});
  const [mediaFilter, setMediaFilter]           = useState('');
  const [mediaType, setMediaType]               = useState('all');
  const [userFilter, setUserFilter]             = useState('');
  const [pendingUser, setPendingUser]           = useState({});
  const { width } = useWindowSize();
  const isMobile  = width < 650;
  const headers   = { Authorization:`Bearer ${token}` };

  // Con la sesión vencida el backend responde 401 con {error}; antes eso se
  // convertía en listas vacías y el panel parecía "sin usuarios / sin contenido".
  const getJson = async (url) => {
    const r = await fetch(url, { headers });
    if (r.status === 401) { onSessionExpired && onSessionExpired(); throw new Error('unauthorized'); }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  };
  const loadUsers     = () => getJson(`${API}/admin/users`).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => setMsg('Error cargando usuarios'));
  const loadMedia     = () => getJson(`${API}/admin/media`).then(d => setMedia(Array.isArray(d) ? d : [])).catch(() => setTmdbMsg('Error cargando contenido'));
  const loadMediaInfo = () => getJson(`${API}/api/media-info`).then(d => setMediaInfo(d || {})).catch(() => {});

  useEffect(() => { loadUsers(); loadMedia(); loadMediaInfo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape cierra el panel, salvo que haya un modal de edición encima.
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !editingMedia) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingMedia, onClose]);

  const createUser  = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { setMsg('Completa todos los campos'); return; }
    if (newUser.password.length < 6) { setMsg('La contraseña debe tener al menos 6 caracteres'); return; }
    let res, data;
    try {
      res  = await fetch(`${API}/admin/users`, { method:'POST', headers:{ ...headers, 'Content-Type':'application/json' }, body: JSON.stringify(newUser) });
      data = await res.json().catch(() => ({}));
    } catch { setMsg('Error de conexión'); return; }
    if (!res.ok) { setMsg(data.error || `Error (${res.status})`); return; }
    setMsg('✓ Usuario creado'); setNewUser({ name:'', email:'', password:'', role:'viewer' }); loadUsers();
  };
  // El backend rechaza p. ej. quitarle el rol al último admin; antes el error se
  // ignoraba y el cambio simplemente "no pegaba" sin explicación.
  const adminCall = async (url, opts) => {
    try {
      const res = await fetch(url, { ...opts, headers:{ ...headers, 'Content-Type':'application/json' } });
      if (res.ok) return true;
      if (res.status === 401) { onSessionExpired && onSessionExpired(); return false; }
      const data = await res.json().catch(() => ({}));
      setMsg(data.error || `Error (${res.status})`);
    } catch { setMsg('Error de conexión'); }
    return false;
  };
  const deleteUser      = async u => {
    if (!window.confirm(`¿Eliminar a ${u.name} (${u.email})? Se borrarán su historial, perfiles y sesiones.`)) return;
    if (await adminCall(`${API}/admin/users/${u.id}`, { method:'DELETE' })) setMsg(`✓ ${u.name} eliminado`);
    loadUsers();
  };
  const changeRole      = async (u, role) => {
    if (role === u.role) return;
    // Quitarse el rol a uno mismo deja el panel inservible (todo responde 403).
    if (u.id === user?.id && !window.confirm('Vas a quitarte el rol de admin a ti mismo y perderás acceso a este panel. ¿Continuar?')) return;
    if (role === 'admin' && u.id !== user?.id && !window.confirm(`¿Dar rol de admin a ${u.name}? Tendrá control total del sistema.`)) return;
    if (await adminCall(`${API}/admin/role`, { method:'POST', body: JSON.stringify({ user_id:u.id, role }) })) {
      setMsg(`✓ ${u.name} ahora es ${role}`);
      if (u.id === user?.id) { onClose(); return; }
    }
    loadUsers();
  };
  // Se bloquean los botones mientras hay una petición en curso: con clics rápidos
  // cada petición partía del mismo valor viejo y el contador "saltaba".
  const setMaxSessions  = async (uid, n) => {
    if (pendingUser[uid]) return;
    setPendingUser(p => ({ ...p, [uid]: true }));
    await adminCall(`${API}/admin/users/${uid}/max-sessions`, { method:'PUT', body: JSON.stringify({ max_sessions: n }) });
    await loadUsers();
    setPendingUser(p => ({ ...p, [uid]: false }));
  };
  const resetPassword   = async u => {
    const pw = window.prompt(`Nueva contraseña para ${u.name} (mínimo 6 caracteres).\nSe cerrarán todas sus sesiones.`);
    if (pw === null) return;
    if (pw.length < 6) { setMsg('La contraseña debe tener al menos 6 caracteres'); return; }
    if (await adminCall(`${API}/admin/users/${u.id}/password`, { method:'PUT', body: JSON.stringify({ password: pw }) })) {
      setMsg(`✓ Contraseña de ${u.name} actualizada`);
      if (u.id === user?.id) { onSessionExpired && onSessionExpired(); return; }
      if (expandedSessions === u.id) refreshSessions(u.id);
    }
    loadUsers();
  };
  const loadSessions    = async (uid) => {
    if (expandedSessions === uid) { setExpandedSessions(null); return; }
    const d = await getJson(`${API}/admin/users/${uid}/sessions`).catch(() => []);
    setSessionsMap(prev => ({ ...prev, [uid]: Array.isArray(d) ? d : [] }));
    setExpandedSessions(uid);
  };
  const refreshSessions = async (uid) => {
    const d = await getJson(`${API}/admin/users/${uid}/sessions`).catch(() => []);
    setSessionsMap(prev => ({ ...prev, [uid]: Array.isArray(d) ? d : [] }));
  };
  // Antes llamaba a loadSessions, que al estar ya expandido lo colapsaba.
  const closeSession    = async (sid, uid) => { await adminCall(`${API}/admin/sessions/${sid}`, { method:'DELETE' }); refreshSessions(uid); loadUsers(); };
  const deleteMedia = async (file, type) => {
    if (!window.confirm(`¿Eliminar "${file}"? Se borrarán también su HLS, carátula, subtítulos e historial.`)) return;
    const res = await fetch(`${API}/admin/media`, { method:'DELETE', headers:{ ...headers, 'Content-Type':'application/json' }, body: JSON.stringify({ file, type }) }).catch(() => null);
    if (!res?.ok) { const d = await res?.json().catch(() => ({})); setTmdbMsg(d?.error || 'Error eliminando archivo'); }
    else setTmdbMsg(`✓ "${file}" eliminado`);
    loadMedia(); loadMediaInfo();
  };

  const applyTMDB = async (m) => {
    const key = m.file;
    setTmdbLoadingMap(prev => ({...prev, [key]: true}));
    setTmdbMsg('');
    try {
      const res = await fetch(`${API}/api/tmdb/apply`, {
        method:'POST',
        headers:{ ...headers, 'Content-Type':'application/json' },
        body: JSON.stringify({ filename: m.file, media_type: m.type })
      });
      const data = await res.json();
      if (res.ok) {
        setTmdbMsg(`✓ "${data.title}" actualizado con carátula oficial`);
        loadMedia(); loadMediaInfo();
      } else {
        setTmdbMsg(res.status === 404 ? `Sin resultado TMDB para "${m.name}"` : (data.error || `Error TMDB (${res.status})`));
      }
    } catch { setTmdbMsg('Error conectando con TMDB'); }
    setTmdbLoadingMap(prev => ({...prev, [key]: false}));
  };

  const scanAllTMDB = async () => {
    if (!window.confirm(`¿Buscar metadata automática en TMDB para ${media.length} archivos? Actualizará títulos, sinopsis, géneros y carátulas.`)) return;
    setScanningTMDB(true); setTmdbMsg('');
    try {
      const res = await fetch(`${API}/admin/tmdb/scan-all`, { method:'POST', headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const ok   = data.results?.filter(r => r.success).length || 0;
      const fail = data.results?.filter(r => !r.success).length || 0;
      setTmdbMsg(`✓ ${ok} actualizados · ${fail} sin resultado en TMDB`);
      loadMedia(); loadMediaInfo();
    } catch { setTmdbMsg('Error conectando con TMDB (si la biblioteca es grande, el proceso pudo seguir en el servidor)'); }
    setScanningTMDB(false);
  };

  const uq = userFilter.trim().toLowerCase();
  const filteredUsers = users.filter(u => !uq || u.name.toLowerCase().includes(uq) || u.email.toLowerCase().includes(uq));
  const roleCount = r => users.filter(u => u.role === r).length;

  const q = mediaFilter.trim().toLowerCase();
  const filteredMedia = media.filter(m =>
    (mediaType === 'all' || m.type === mediaType) &&
    (!q || m.file.toLowerCase().includes(q) || (mediaInfo[m.file]?.title || '').toLowerCase().includes(q))
  );

  const inp = { width:'100%', padding:'12px 16px', borderRadius:'8px', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.accentBorder}`, color:C.text, fontSize:'14px', outline:'none', boxSizing:'border-box', fontFamily:'Inter, sans-serif', marginBottom:'12px' };
  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} style={{ padding: isMobile ? '8px 10px' : '10px 18px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize: isMobile ? '11px' : '13px', fontWeight:'500', fontFamily:'Inter, sans-serif', background: tab === id ? `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})` : C.accentDim, color: tab === id ? '#fff' : C.textMuted, whiteSpace:'nowrap' }}>
      {label}
    </button>
  );

  return (
    <>
      {editingMedia && <EditInfoModal media={editingMedia} info={mediaInfo[editingMedia.file]} token={token} onClose={() => setEditingMedia(null)} onSaved={() => { loadMediaInfo(); setEditingMedia(null); }} />}
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.9)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', padding: isMobile ? '0' : '20px' }}>
        <div style={{ background:C.bg, border: isMobile ? 'none' : `1px solid ${C.border}`, borderRadius: isMobile ? '0' : '20px', width:'100%', maxWidth:'960px', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '92vh', overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding: isMobile ? '14px 16px' : '24px 32px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            <h2 style={{ fontFamily:'Playfair Display, serif', fontSize: isMobile ? '17px' : '22px', color:C.text }}>Panel Admin</h2>
            <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${C.accentBorder}`, color:C.textMuted, padding:'7px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'13px' }}>✕</button>
          </div>
          <div style={{ padding: isMobile ? '10px 12px' : '14px 32px', borderBottom:`1px solid ${C.border}`, display:'flex', gap:'6px', overflowX:'auto', flexShrink:0 }}>
            {tabBtn('users','👥 Usuarios')}
            {tabBtn('media','🎬 Contenido')}
            {tabBtn('upload','⬆ Subir')}
            {tabBtn('inbox','💬 Mensajes')}
            {tabBtn('subs','🔤 Subtítulos')}
            {tabBtn('stats','📊 Stats')}
          </div>
          <div style={{ padding: isMobile ? '14px' : '24px 32px', overflowY: tab === 'inbox' ? 'hidden' : 'auto', overflowX: 'hidden', flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>

            {tab === 'users' && (
              <div>
                <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', marginBottom:'14px' }}>Crear usuario</h3>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'8px', marginBottom:'10px' }}>
                  <input placeholder="Nombre" value={newUser.name} onChange={e => setNewUser({...newUser, name:e.target.value})} style={inp} />
                  <input placeholder="Correo" type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email:e.target.value})} style={inp} />
                  <input placeholder="Contraseña (mín. 6)" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password:e.target.value})} onKeyDown={e => e.key === 'Enter' && createUser()} style={inp} />
                  <select value={newUser.role} onChange={e => setNewUser({...newUser, role:e.target.value})} style={{...inp}}>
                    <option value="viewer">Viewer</option>
                    <option value="uploader">Uploader</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button onClick={createUser} style={{ padding:'9px 20px', background:`linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, border:'none', color:'#fff', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', marginBottom:'18px' }}>Crear usuario</button>
                {msg && <p style={{ color: msg.includes('✓') ? C.success : '#e07a5f', fontSize:'13px', marginBottom:'14px' }}>{msg}</p>}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:'6px', marginBottom:'10px' }}>
                  <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600' }}>Usuarios ({filteredUsers.length === users.length ? users.length : `${filteredUsers.length} de ${users.length}`})</h3>
                  <span style={{ color:C.textDim, fontSize:'11px' }}>{roleCount('admin')} admin · {roleCount('uploader')} uploader · {roleCount('viewer')} viewer</span>
                </div>
                <input placeholder="Buscar por nombre o correo..." value={userFilter} onChange={e => setUserFilter(e.target.value)} style={inp} />
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  {filteredUsers.length === 0 && <p style={{ color:C.textDim, fontSize:'13px', textAlign:'center', padding:'20px 0' }}>{users.length === 0 ? 'Sin usuarios' : 'Sin resultados'}</p>}
                  {filteredUsers.map(u => (
                    <div key={u.id} style={{ background:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, overflow:'hidden' }}>
                      <div style={{ padding:'12px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
                        <div>
                          <p style={{ color:C.text, fontSize:'13px', fontWeight:'500' }}>{u.name}{u.id === user?.id && <span style={{ color:C.textDim, fontWeight:'400' }}> (tú)</span>}</p>
                          <p style={{ color:C.textMuted, fontSize:'11px', marginTop:'2px' }}>{u.email}</p>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                          {/* Dispositivos activos / límite */}
                          <div style={{ display:'flex', alignItems:'center', gap:'4px', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'3px 6px' }}>
                            <span style={{ color: u.active_sessions >= (u.max_sessions||2) ? C.danger : C.success, fontSize:'11px', fontWeight:'600' }}>
                              📱 {u.active_sessions||0}/{u.max_sessions||2}
                            </span>
                            <button title="Menos dispositivos" onClick={() => setMaxSessions(u.id, Math.max(1,(u.max_sessions||2)-1))} disabled={pendingUser[u.id] || (u.max_sessions||2) <= 1} style={{ background:'transparent', border:'none', color:C.textMuted, cursor:'pointer', fontSize:'12px', padding:'0 2px', lineHeight:1 }}>−</button>
                            <button title="Más dispositivos" onClick={() => setMaxSessions(u.id, Math.min(10,(u.max_sessions||2)+1))} disabled={pendingUser[u.id] || (u.max_sessions||2) >= 10} style={{ background:'transparent', border:'none', color:C.textMuted, cursor:'pointer', fontSize:'12px', padding:'0 2px', lineHeight:1 }}>+</button>
                          </div>
                          <select value={u.role} onChange={e => changeRole(u, e.target.value)} style={{ background:C.bgCard, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, padding:'4px 8px', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>
                            <option value="viewer">Viewer</option>
                            <option value="uploader">Uploader</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button onClick={() => loadSessions(u.id)} style={{ background: expandedSessions===u.id ? C.accentDim : 'rgba(255,255,255,0.04)', border:`1px solid ${expandedSessions===u.id ? C.accentBorder : C.border}`, color: expandedSessions===u.id ? C.accentSoft : C.textMuted, padding:'4px 8px', borderRadius:'6px', cursor:'pointer', fontSize:'11px' }}>
                            {expandedSessions===u.id ? '▲ Ocultar' : '▼ Dispositivos'}
                          </button>
                          <button onClick={() => resetPassword(u)} title="Restablecer contraseña" style={{ background:'rgba(255,255,255,0.04)', border:`1px solid ${C.border}`, color:C.textMuted, padding:'4px 8px', borderRadius:'6px', cursor:'pointer', fontSize:'11px' }}>🔑 Contraseña</button>
                          <button onClick={() => deleteUser(u)} disabled={u.id === user?.id} title={u.id === user?.id ? 'No puedes eliminar tu propia cuenta' : undefined} style={{ opacity: u.id === user?.id ? 0.4 : 1, cursor: u.id === user?.id ? 'not-allowed' : 'pointer', background:C.dangerDim, border:`1px solid rgba(192,57,43,0.3)`, color:C.danger, padding:'4px 8px', borderRadius:'6px', fontSize:'11px' }}>Eliminar</button>
                        </div>
                      </div>
                      {/* Sesiones activas expandidas */}
                      {expandedSessions === u.id && (
                        <div style={{ borderTop:`1px solid ${C.border}`, padding:'10px 14px', display:'flex', flexDirection:'column', gap:'6px', background:'rgba(0,0,0,0.2)' }}>
                          {(sessionsMap[u.id]||[]).length === 0
                            ? <p style={{ color:C.textDim, fontSize:'12px', fontFamily:'Inter, sans-serif' }}>Sin sesiones activas</p>
                            : (sessionsMap[u.id]||[]).map(s => (
                              <div key={s.id} style={{ display:'flex', alignItems:'center', gap:'10px', justifyContent:'space-between', flexWrap:'wrap' }}>
                                <div>
                                  <p style={{ color:C.text, fontSize:'11px', fontFamily:'Inter, sans-serif' }}>{s.ip || 'IP desconocida'}</p>
                                  <p style={{ color:C.textDim, fontSize:'10px', marginTop:'2px', maxWidth:'360px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.user_agent || 'Dispositivo desconocido'}</p>
                                  <p style={{ color:C.textDim, fontSize:'10px' }}>Desde {s.created_at?.slice(0,16).replace('T',' ')}</p>
                                </div>
                                <button onClick={() => closeSession(s.id, u.id)} style={{ background:C.dangerDim, border:`1px solid rgba(192,57,43,0.3)`, color:C.danger, padding:'4px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'11px', flexShrink:0 }}>
                                  Cerrar sesión
                                </button>
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'media' && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px', flexWrap:'wrap', gap:'10px' }}>
                  <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600' }}>Contenido ({filteredMedia.length === media.length ? media.length : `${filteredMedia.length} de ${media.length}`})</h3>
                  <button onClick={scanAllTMDB} disabled={scanningTMDB} style={{ padding:'7px 14px', background: scanningTMDB ? C.textDim : C.tmdbDim, border:`1px solid ${C.tmdbBorder}`, color: scanningTMDB ? C.textDim : C.tmdb, borderRadius:'8px', cursor: scanningTMDB ? 'not-allowed' : 'pointer', fontSize:'12px', fontWeight:'600', display:'flex', alignItems:'center', gap:'5px' }}>
                    {scanningTMDB ? '🔄 Buscando en TMDB...' : '🎬 TMDB: Actualizar todo'}
                  </button>
                </div>
                {tmdbMsg && (
                  <div style={{ padding:'10px 14px', background: tmdbMsg.includes('✓') ? C.successDim : C.dangerDim, border:`1px solid ${tmdbMsg.includes('✓') ? 'rgba(39,174,96,0.3)' : 'rgba(192,57,43,0.3)'}`, borderRadius:'8px', marginBottom:'14px' }}>
                    <p style={{ color: tmdbMsg.includes('✓') ? C.success : C.danger, fontSize:'13px' }}>{tmdbMsg}</p>
                  </div>
                )}
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                  <input placeholder="Buscar por título o archivo..." value={mediaFilter} onChange={e => setMediaFilter(e.target.value)} style={{ ...inp, marginBottom:0, flex:1 }} />
                  <select value={mediaType} onChange={e => setMediaType(e.target.value)} style={{ ...inp, marginBottom:0, width:'auto', cursor:'pointer' }}>
                    <option value="all">Todo</option>
                    <option value="movie">Películas</option>
                    <option value="series">Series</option>
                  </select>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  {filteredMedia.length === 0 && <p style={{ color:C.textDim, fontSize:'13px', textAlign:'center', padding:'20px 0' }}>{media.length === 0 ? 'Sin contenido' : 'Sin resultados'}</p>}
                  {filteredMedia.map(m => (
                    // m.id se reinicia en 1 para películas y para series: no es único.
                    <div key={`${m.type}:${m.file}`} style={{ padding:'10px 14px', background:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}` }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                          {m.thumbnail && (
                            <img src={`${API}/thumbnail/${encodeURIComponent(m.thumbnail)}`} alt={m.name} loading="lazy" style={{ width:'60px', height:'36px', objectFit:'cover', borderRadius:'5px', border:`1px solid ${C.border}` }} />
                          )}
                          <div>
                            <p style={{ color:C.text, fontSize:'12px', fontWeight:'500' }}>{mediaInfo[m.file]?.title || m.name}</p>
                            <p style={{ color:C.textMuted, fontSize:'10px', marginTop:'2px' }}>
                              {m.type==='movie'?'Película':'Serie'} · {formatSize(m.size)}
                              {mediaInfo[m.file]?.genre ? ` · ${mediaInfo[m.file].genre}` : ''}
                              {mediaInfo[m.file]?.year > 0 ? ` · ${mediaInfo[m.file].year}` : ''}
                            </p>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                          <button onClick={() => applyTMDB(m)} disabled={tmdbLoadingMap[m.file]} title="Buscar metadata y carátula en TMDB" style={{ padding:'5px 10px', background: tmdbLoadingMap[m.file] ? C.textDim : C.tmdbDim, border:`1px solid ${C.tmdbBorder}`, color: tmdbLoadingMap[m.file] ? C.textDim : C.tmdb, borderRadius:'6px', cursor: tmdbLoadingMap[m.file] ? 'not-allowed' : 'pointer', fontSize:'11px', fontWeight:'500' }}>
                            {tmdbLoadingMap[m.file] ? '🔄' : '🎬 TMDB'}
                          </button>
                          <button onClick={() => setEditingMedia(m)} style={{ background:C.accentDim, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'11px' }}>✏ Info</button>
                          <button onClick={() => deleteMedia(m.file, m.type)} style={{ background:C.dangerDim, border:`1px solid rgba(192,57,43,0.3)`, color:C.danger, padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'11px' }}>Eliminar</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'upload' && <UploadTab token={token} onDone={() => { loadMedia(); onUploadDone && onUploadDone(); }} />}
            {tab === 'inbox'  && <AdminInboxTab token={token} />}
            {tab === 'subs'   && <AdminSubtitlesTab token={token} media={media} mediaInfo={mediaInfo} />}
            {tab === 'stats'  && <AdminStatsTab token={token} />}
          </div>
        </div>
      </div>
    </>
  );
}
